import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { extractChildrenCssBySelector, extractSiblingsCssBySelector, extractCssContextBySelector, extractComponentCssBySelector, extractParentCssBySelector, extractParentHtmlBySelector, extractChildrenHtmlBySelector, extractSiblingsHtmlBySelector, extractHtmlContextBySelector, buildEditContextFromPlan, extractCssGlobalBlocks, extractAllClassNames, extractHtmlBySelector, extractCssBlocksForSelector } from "../localFunctions";

// ✅ UPDATED IMPORTS (whole-page HTML/CSS)
import {
  generateFullPageHtmlCssFromAI,
  // editFullPageHtmlCssFromAI,
  getUserIntent,
  getComponentsToEditFromAI,
  updateWebsiteMeta,
} from "../kairosFunctions";

import { CodeProjectPreview } from "../codeRenderer";

// (kept imports to avoid breaking, even if unused now)
import {
  createMessage,
} from "../firebaseHelpers";

import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { FaHome, FaComments, FaCog, FaPaperPlane, FaTimesCircle } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import "./KairosEditor.css";

const KairosEditor = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const { websiteId } = useParams();

  const userId = currentUser?.uid;

  // UI state
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [latestUserData, setLatestUserData] = useState(userData);
  const [classNames, setClassNames] = useState([]);
  const [selectedSelector, setSelectedSelector] = useState(""); // store like ".hero"
  // example values – replace with your real logic values
  const [estimatedTokens, setEstimatedTokens] = useState(0);
  const [estimatedCredits, setEstimatedCredits] = useState(0); // optional if you have it

  // ✅ Separate refs for desktop panel textarea vs mobile chatbar textarea
  const leftTextareaRef = useRef(null);
  const mobileTextareaRef = useRef(null);

  const chatScrollRef = useRef(null);
  // const autoGenOnceRef = useRef(false);
  const iframeRef = useRef(null);
  // Page state (NOW: whole-page html/css)
  const [page, setPage] = useState(null);

  // Data
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [messages, setMessages] = useState([]);

  // NEW: selected component to enable cheaper edits (kept, but not used in full-page mode)
  const [selectedComponentId, setSelectedComponentId] = useState(null);

  // NEW: keep preview iframe ref (for click-to-select later)
  const previewIframeRef = useRef(null);

  // Derived
  const websiteName = useMemo(() => {
    return websiteSettings?.name || "Untitled Website";
  }, [websiteSettings]);

  const autoResizeTextarea = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const clearInput = () => {
    setPrompt("");
    if (leftTextareaRef.current) leftTextareaRef.current.style.height = "auto";
    if (mobileTextareaRef.current) mobileTextareaRef.current.style.height = "auto";
  };

  const flattenTree = useCallback((nodes, out = []) => {
    if (!Array.isArray(nodes)) return out;
    for (const n of nodes) {
      out.push({ id: String(n.id), type: String(n.type) });
      if (Array.isArray(n.children)) flattenTree(n.children, out);
    }
    return out;
  }, []);

  // ✅ UPDATED: Generate whole page HTML/CSS (simple mode)
  const handleGeneratePage = useCallback(
    async ({ auto = false } = {}, userIntent ) => {
      if (!userId || !websiteId) return;

      try {
        setPage(null);
        setGenerating(true);

        // 1) Ask AI for FULL PAGE html/css
        const raw = await generateFullPageHtmlCssFromAI({
          websiteName,
          websiteDescription: websiteSettings?.description || "",
          userRequest: websiteSettings?.description || "",
          userId,
          websiteId,
          action: userIntent,
        });

        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

        const html = typeof parsed?.html === "string" ? parsed.html : "";
        const css = typeof parsed?.css === "string" ? parsed.css : "";
        const pageSummary = typeof parsed?.pageSummary === "string" ? parsed.pageSummary : "";

        if (!html) throw new Error("AI invalid: missing html");

        // 2) Save to /pages/home (single doc fields)
        const pageRef = doc(db, "workspaces", userId, "websites", websiteId, "pages", "home");
        await setDoc(
          pageRef,
          {
            pageId: "home",
            html,
            css,
            pageSummary,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(), // only for first write (good enough here)
          },
          { merge: true }
        );

        // 3) Update local state
        setPage({ id: "home", html, css });

        await createMessage({
          userId,
          websiteId,
          messageType: "edit",
          who: "Kairos",
          message: "Done ✅ I generated your page. Tell me what to change.",
          action: "Generate",
        });
      } catch (e) {
        console.error("handleGeneratePage error:", e);

        await createMessage({
          userId,
          websiteId,
          messageType: "edit",
          who: "Kairos",
          message: "Sorry — I couldn’t generate the page. Please try again.",
          action: "Error",
        });
      } finally {
        setGenerating(false);
        clearInput();
      }
    },
    [userId, websiteId, websiteName, websiteSettings?.description]
  );

  // const maybeAutoGenerate = useCallback(
  //   ({ exists }) => {
  //     const isEmpty = !exists;
  //     if (!autoGenOnceRef.current && isEmpty) {
  //       autoGenOnceRef.current = true;
  //       handleGeneratePage({ auto: true });
  //       return;
  //     }
  //   },
  //   [handleGeneratePage]
  // );

  // Listen for /pages/home existence (auto-generate if missing)
  // useEffect(() => {
  //   if (!userId || !websiteId) return;

  //   const pageRef = doc(db, "workspaces", userId, "websites", websiteId, "pages", "home");
  //   if (latestUserData?.credits > 15) {
  //     const unsub = onSnapshot(
  //       pageRef,
  //       (snap) => {
  //         if (!snap.exists()) {
  //           maybeAutoGenerate({ exists: false });
  //           return;
  //         }

  //         const data = snap.data() || {};

  //         // ✅ Simple mode: page doc holds html/css directly
  //         setPage({
  //           id: "home",
  //           html: data.html || "",
  //           css: data.css || "",
  //         });
  //       },
  //       (err) => console.error("pages/home listener error:", err)
  //     );
  //     return () => unsub();
  //   }
  //   else {
  //     setShowCreditsModal(true);
  //     const unsub = onSnapshot(
  //       pageRef,
  //       (snap) => {
  //         const data = snap.data() || {};

  //         // ✅ Simple mode: page doc holds html/css directly
  //         setPage({
  //           id: "home",
  //           html: data.html || "",
  //           css: data.css || "",
  //           pageSummary: "",
  //           updatedAt: serverTimestamp(),
  //         });
  //       },
  //       (err) => console.error("pages/home listener error:", err)
  //     );
  //     return () => unsub();
  //   }
  // }, [userId, websiteId, latestUserData?.credits, maybeAutoGenerate]);

  // Close drawers on desktop resize
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) {
        setLeftOpen(false);
        setRightOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Auth redirect
  useEffect(() => {
    if (currentUser === undefined) return;
    if (currentUser === null) navigate("/");
  }, [currentUser, navigate]);

  // Website settings listener
  useEffect(() => {
    if (!userId || !websiteId) return;

    const settingsRef = doc(db, "workspaces", userId, "websites", websiteId, "websiteSettings", "general");

    const unsub = onSnapshot(
      settingsRef,
      (snap) => setWebsiteSettings(snap.exists() ? snap.data() : null),
      (err) => console.error("websiteSettings listener error:", err)
    );

    return () => unsub();
  }, [userId, websiteId]);
  
  // userData listener
  useEffect(() => {
    if (!userId) return;

    const userRef = doc(db, "users", userId);

    const unsub = onSnapshot(
      userRef,
      (snap) => setLatestUserData(snap.exists() ? snap.data() : null),
      (err) => console.error("userData listener error:", err)
    );

    return () => unsub();
  }, [userId]);

  useEffect(() => {
    const htmlString = page?.html || ""; // adjust if your page stores html differently
    if (!htmlString) return;
  
    const names = extractAllClassNames(htmlString);
    setClassNames(names);
  
    // optional: set default selection
    if (!selectedSelector && names.length > 0) {
      setSelectedSelector(`.${names[0]}`);
    }
  }, [page?.html, selectedSelector]); // or [page] if needed

  // Messages listener
  useEffect(() => {
    if (!userId || !websiteId) return;

    const msgCol = collection(db, "workspaces", userId, "websites", websiteId, "messages");
    const q = query(msgCol, orderBy("createdAt", "asc"));

    const unsub = onSnapshot(
      q,
      (snap) => setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("messages listener error:", err)
    );

    return () => unsub();
  }, [userId, websiteId]);

  // Scroll chat to bottom when messages change
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    function onMessage(e) {
      const iframeWin = previewIframeRef.current?.contentWindow;
      if (iframeWin && e.source !== iframeWin) return;

      if (e?.data?.type === "KAIROS_SELECT" && e.data.componentId) {
        setSelectedComponentId(String(e.data.componentId));
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // ✅ UPDATED: Edit WHOLE page HTML/CSS (simple mode)
  const handleEditWebsite = useCallback(async ({ userIntent }) => {
    const text = prompt?.trim();
    if (!text) return;
    if (!userId || !websiteId) return;
    if (!page?.html) throw new Error("No page HTML loaded yet");

    // Determine the type of edit
    const selectorList = extractAllClassNames(page.html || "");
    const pageSummary = page?.pageSummary || "";
    const html = page?.html || "";

    const componentsToEdit = await getComponentsToEditFromAI({
      userMessage: text,
      pageSummary,
      selectorList,
      userId,
      websiteId,
      html,
    });

    const editContext = buildEditContextFromPlan({
      page,
      plan: componentsToEdit,
    });console.log("EDIT CONTEXT:", editContext);

    clearInput();
    // setGenerating(true);

    // try {
    //   const raw = await editFullPageHtmlCssFromAI({
    //     userRequest: text,
    //     currentHtml: page.html,
    //     currentCss: page.css || "",
    //     userId,
    //     websiteId,
    //     action: userIntent,
    //   });

    //   const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

    //   const nextHtml = typeof parsed?.html === "string" ? parsed.html : "";
    //   const nextCss = typeof parsed?.css === "string" ? parsed.css : "";

    //   if (!nextHtml) throw new Error("AI invalid: missing html");

    //   const pageRef = doc(db, "workspaces", userId, "websites", websiteId, "pages", "home");
    //   await setDoc(
    //     pageRef,
    //     {
    //       html: nextHtml,
    //       css: nextCss,
    //       updatedAt: serverTimestamp(),
    //     },
    //     { merge: true }
    //   );

    //   setPage({ id: "home", html: nextHtml, css: nextCss });

    //   await createMessage({
    //     userId,
    //     websiteId,
    //     messageType: "edit",
    //     who: "Kairos",
    //     message: "Done ✅ Updated your page.",
    //     action: "Edit",
    //   });


    //   setSelectedComponentId(null);
    // } catch (e) {
    //   console.error("handleEditWebsite error:", e);
    //   await createMessage({
    //     userId,
    //     websiteId,
    //     messageType: "edit",
    //     who: "Kairos",
    //     message: "Sorry — I couldn’t apply that edit.",
    //     action: "EditError",
    //   });
    // } finally {
    //   setGenerating(false);
    // }
  }, [prompt, userId, websiteId, page]);

  // Decide intent (Edit vs Settings etc)
  const detectUserIntent = useCallback(async () => {
    const text = prompt?.trim();
    if (!text) return;
    if (!userId || !websiteId) return;

    let userCredits = latestUserData?.credits;

    if (userCredits > 1) {
      try {
        await createMessage({
          userId,
          websiteId,
          messageType: "Edit",
          who: "User",
          message: text || "",
          action: text || "Edit",
        });

        const userMessage = text.trim();
        const chattingName = (latestUserData?.name || "There").trim();

        console.log("getUserIntent inputs:", { userMessage, chattingName });

        const meta = await getUserIntent({userMessage, chattingName, userId, websiteId});
        userCredits = latestUserData?.credits;
        const safeMeta = typeof meta === "string" ? JSON.parse(meta) : meta;

        const userIntent = safeMeta.userIntent;
        const estimatedTokens = Number(safeMeta.estimatedTokens);
        setEstimatedTokens(estimatedTokens);
        const estimatedCredits = estimatedTokens / 1000;
        setEstimatedCredits(estimatedCredits);

        console.log("User Credits: ", userCredits, "\nEstimate Tokens:", estimatedTokens, "\nEstimated Credits:", estimatedCredits);

        if (userIntent === "Meta") {
          if (userCredits > estimatedCredits) {
            setGenerating(true);
            const updateData = await updateWebsiteMeta({currentWebsiteName: websiteName, currentWebsiteDescription: websiteSettings?.description || "", userMessage, chattingName, userId, websiteId, action: userIntent})
            userCredits = latestUserData?.credits;
            const safeUpdateData = typeof updateData === "string" ? JSON.parse(updateData) : updateData;
            const updateFriendlyResponse = safeUpdateData.friendlyResponse?.trim() || "Done ✅ I updated your description.";
            console.log("New name: ", safeUpdateData.newWebsiteName, "\nNew description:", safeUpdateData.newWebsiteDescription, "\nAI friendly response: ", safeUpdateData.friendlyResponse);
            const newName = safeUpdateData.newWebsiteName || websiteName;
            const newDescription = safeUpdateData.newWebsiteDescription || websiteSettings?.description;

            console.log("\n\n\nNew name: ", newName, "\nNew description:", newDescription);

            await updateDoc(
              doc(db, "workspaces", userId, "websites", websiteId, "websiteSettings", "general"),
              {
                name: newName,
                description: newDescription,
                updateAt: serverTimestamp(),
              }
            );
            await updateDoc(
              doc(db, "workspaces", userId, "websites", websiteId),
              {
                name: newName,
                description: newDescription,
                updateAt: serverTimestamp(),
              }
            );

            await createMessage({
              userId,
              websiteId,
              messageType: "Edit",
              who: "Kairos",
              message: updateFriendlyResponse,
              action: "Edit",
            });
          }else {
            setShowCreditsModal(true);
            await createMessage({
              userId,
              websiteId,
              messageType: "Edit",
              who: "Kairos",
              message: `Sorry ${latestUserData?.name || ""}, you don't have enough credits to perform this action.`,
              action: "Edit",
            });
          };
          clearInput();
          return;
        } 
        
        else if (userIntent === "Regenerate") {
          if (userCredits >  estimatedCredits && userCredits >= 25) {
            setGenerating(true);
            await handleGeneratePage({ auto: false, userIntent });
            userCredits = latestUserData?.credits;
          }
          setShowCreditsModal(true);
          userCredits = latestUserData?.credits;
          await createMessage({
            userId,
            websiteId,
            messageType: "Edit",
            who: "Kairos",
            message: `Sorry ${latestUserData?.name || ""}, you don't have enough credits to perform this action.`,
            action: "Edit",
          });
          clearInput();
          return;
        } 
        
        else if (userIntent === "Edit") {
          if (userCredits > estimatedCredits && userCredits >= 25) {
            setGenerating(true);
            await handleEditWebsite({ userIntent });
            userCredits = latestUserData?.credits;
          }
          setShowCreditsModal(true);
          userCredits = latestUserData?.credits;
          await createMessage({
            userId,
            websiteId,
            messageType: "Edit",
            who: "Kairos",
            message: `Sorry ${latestUserData?.name || ""}, you don't have enough credits to perform this action.`,
            action: "Edit",
          });
          clearInput();
          return;
        }

        else if (userIntent === "Unclear") {
          userCredits = latestUserData?.credits;
          await createMessage({
            userId,
            websiteId,
            messageType: "Edit",
            who: "Kairos",
            message: `Sorry ${latestUserData?.name || ""}, your message was bit unclear, please try to be very specif, or use component labels as indicated on the editor view.`,
            action: "Edit",
          })
        }

        else if (userIntent === "Upcoming") {
          userCredits = latestUserData?.credits;
          await createMessage({
            userId,
            websiteId,
            messageType: "Edit",
            who: "Kairos",
            message: `Sorry ${latestUserData?.name || ""}, I can't do that as yet, I will tell you when I can.`,
            action: "Edit",
          })
        };
        clearInput();
      } catch (error) {
        console.error("[detectUserIntent] AI ERROR:", error);
      } finally {
        setGenerating(false);
      }
    } else {
      await createMessage({
        userId,
        websiteId,
        messageType: "Edit",
        who: "Kairos",
        message: `Sorry ${latestUserData?.name || ""}, you don't have enough credits to perform this action.`,
        action: "Edit",
      });
      userCredits = latestUserData?.credits;
      setShowCreditsModal(true);
    };
  }, [prompt, userId, websiteId, latestUserData?.credits, latestUserData?.name, websiteName, websiteSettings?.description, handleEditWebsite, handleGeneratePage]);

  return (
    <main className="editor-root">
      {/* Sticky top bar (mobile) */}
      <header className="editor-topbar">
        {selectedComponentId && (
          <div className="editor-selected">
            Selected: <strong>{selectedComponentId}</strong>
            <button onClick={() => setSelectedComponentId(null)} className="editor-clear-selected">
              Clear
            </button>
          </div>
        )}

        <button
          className="editor-topbtn"
          onClick={() => {
            setLeftOpen(true);
            setRightOpen(false);
          }}
          aria-label="Open conversations"
        >
          <FaComments />
        </button>

        <button className="editor-topbtn" onClick={() => navigate("/workspace")} aria-label="Back to Workspaces">
          <FaHome />
        </button>

        <div className="editor-title">
          <span className="editor-title-text">{websiteName}</span>
        </div>

        <button
          className="editor-topbtn"
          onClick={() => {
            setRightOpen(true);
            setLeftOpen(false);
          }}
          aria-label="Open settings"
        >
          <FaCog />
        </button>
      </header>

      {/* Mobile overlays */}
      {leftOpen && <div className="editor-overlay" onClick={() => setLeftOpen(false)} />}
      {rightOpen && <div className="editor-overlay" onClick={() => setRightOpen(false)} />}

      {/* LEFT: Conversations */}
      <aside className={`editor-left ${leftOpen ? "open" : ""}`}>
        <div className="panel-glass">
          <div className="panel-header">
            <h3>Conversations</h3>

            <button
              className="panel-home"
              onClick={() => navigate("/workspace")}
              aria-label="Back to Workspaces"
              title="Back"
            >
              <FaHome />
            </button>

            <FaTimesCircle className="panel-close" onClick={() => setLeftOpen(false)} />
          </div>

          <div className="panel-body">
            <div className="chat-viewer" ref={chatScrollRef}>
              {messages.length === 0 ? (
                <p className="muted">No messages yet.</p>
              ) : (
                messages.map((m) => {
                  const isUser =
                    (m.senderType && m.senderType.toLowerCase() === "user") ||
                    (m.sender && m.sender.toLowerCase() === "user") ||
                    (m.who && m.who.toLowerCase() === "user");

                  const ts = m.createdAt?.toDate ? m.createdAt.toDate() : null;

                  const timeLabel = ts
                    ? ts.toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "";

                  return (
                    <div key={m.id} className={`chat-row ${isUser ? "is-user" : "is-ai"}`}>
                      <div className={`chat-bubble ${isUser ? "bubble-user" : "bubble-ai"}`}>
                        <div className="chat-msg-text">{m.message || m.text}</div>
                        <div className="chat-msg-time">{timeLabel}</div>
                      </div>
                    </div>
                  );
                })
              )}

              {generating && (
                <div className="chat-row is-ai">
                  <div className="chat-working">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                    <span className="chat-working-text">Kairos is working…</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="panel-footer">
            {messages?.length > 0 && (
              <div className="mobile-last-msg">
                <div className="mobile-last-msg-label">
                  {(() => {
                    const last = messages[messages.length - 1];
                    const isUser =
                      (last.senderType && last.senderType.toLowerCase() === "user") ||
                      (last.sender && last.sender.toLowerCase() === "user") ||
                      (last.who && last.who.toLowerCase() === "user");
                    return isUser ? "You" : "Kairos";
                  })()}
                </div>

                <div className="mobile-last-msg-text">
                  {(() => {
                    const last = messages[messages.length - 1];
                    return (last.message || last.text || "").toString();
                  })()}
                </div>

                {generating && <div className="mobile-last-msg-working">Kairos is working…</div>}
              </div>
            )}

            <div className="chatbox">
              <textarea
                ref={leftTextareaRef}
                className="chatbox-input"
                rows={1}
                placeholder="Text to edit..."
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);
                  autoResizeTextarea(e.target);
                }}
                onInput={(e) => autoResizeTextarea(e.target)}
                disabled={generating}
              />
              <button className="chatbox-send" onClick={detectUserIntent} aria-label="Send" disabled={generating}>
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* CENTER: Renderer */}
      <section className="editor-center">
        <div className="renderer-shell">
          {generating ? (
            <div className="render-placeholder">
              <div className="render-placeholder-title">
                Generating your {websiteName}…
              </div>
              <div className="render-placeholder-subtitle">
                You will be up and running soon...
              </div>
            </div>
          ) : websiteId ? (
            <CodeProjectPreview
              userId={userId}
              websiteId={websiteId}
              iframeRef={iframeRef}
              title="Kairos Preview"
            />
          ) : (
            <div className="render-placeholder">
              <div className="render-placeholder-title">No website yet</div>
              <div className="render-placeholder-subtitle">
                Create a website to see the preview here.
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mobile chatbar */}
      <div className="mobile-chatbar">
        <div className="chatbox">
          <textarea
            ref={mobileTextareaRef}
            className="chatbox-input"
            rows={1}
            placeholder="Text to edit..."
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              autoResizeTextarea(e.target);
            }}
            onInput={(e) => autoResizeTextarea(e.target)}
            disabled={generating}
          />
          <button className="chatbox-send" onClick={detectUserIntent} aria-label="Send" disabled={generating}>
            <FaPaperPlane />
          </button>
        </div>
      </div>

      {/* RIGHT: Settings */}
      <aside className={`editor-right ${rightOpen ? "open" : ""}`}>
        <div className="panel-glass">
          <div className="panel-header">
            <h3>Settings</h3>
            {selectedComponentId && (
              <div className="editor-selected">
                Selected: <strong>{selectedComponentId}</strong>
                <button onClick={() => setSelectedComponentId(null)} className="editor-clear-selected">
                  Clear
                </button>
              </div>
            )}
            <FaTimesCircle className="panel-close" onClick={() => setRightOpen(false)} />
          </div>

          <div className="panel-body">
            <div className="settings-card">
              <h4>Website</h4>
              <div className="kv">
                <span>Name</span>
                <span className="kv-value">{websiteName}</span>
              </div>
              <div className="kv">
                <span>Status</span>
                <span className="kv-value pill small">Draft</span>
              </div>
            </div>

            <div className="settings-card">
              <h4>Publishing</h4>
              <p className="muted">Next: connect domain, publish, and manage versions here.</p>

              <button className="primary-btn" onClick={() => alert("Publish coming next")}>
                Publish
              </button>

              <button className="ghost-btn" onClick={() => alert("Preview coming next")}>
                Preview link
              </button>
            </div>

            <div className="settings-card">
              <h4>Editor</h4>
              <div className="kv">
                <span>Canvas</span>
                <span className="kv-value">Desktop</span>
              </div>
              <div className="kv">
                <span>Autosave</span>
                <span className="kv-value pill small">On</span>
              </div>
            </div>

            <div style={{ padding: 12 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Select a component class</label>

              <select
                value={selectedSelector}
                onChange={(e) => setSelectedSelector(e.target.value)}
                style={{ width: "100%", padding: 8 }}
              >
                <option value="">-- Select --</option>
                {classNames.map((c) => (
                  <option key={c} value={`.${c}`}>
                    {c}
                  </option>
                ))}
              </select>

              <button
                style={{ marginTop: 10, padding: 8, width: "100%" }}
                onClick={() => {
                  const htmlString = page?.html || "";
                  const cssString = page?.css || "";

                  console.log("SELECTED:", selectedSelector);
                  console.log("\nHTML:", extractHtmlBySelector(htmlString, selectedSelector));
                  console.log("\nCSS:", extractCssBlocksForSelector(cssString, selectedSelector));
                  console.log("\nGLOBAL CSS: ", extractCssGlobalBlocks(cssString));
                  console.log("\nPARENT HTML: ",extractParentHtmlBySelector(htmlString, selectedSelector));
                  console.log("\nCHILDREN HTML: ",extractChildrenHtmlBySelector(htmlString, selectedSelector));
                  console.log("\nSIMBLINGS HTML: ",extractSiblingsHtmlBySelector(htmlString, selectedSelector));
                  console.log("\nCONTEXT: ",extractHtmlContextBySelector(htmlString, selectedSelector));
                  console.log("\nCOMPONENT CSS: ", extractComponentCssBySelector(htmlString, cssString, selectedSelector));
                  console.log("\nPARENT CSS: ", extractParentCssBySelector(htmlString, cssString, selectedSelector));
                  console.log("\nCHILDREN CSS: ", extractChildrenCssBySelector(htmlString, cssString, selectedSelector));
                  console.log("\nSIBLINGS CSS: ", extractSiblingsCssBySelector(htmlString, cssString, selectedSelector));
                  console.log("\nCSS CONTEXT: ", extractCssContextBySelector(htmlString, cssString, selectedSelector));
                }}
                disabled={!selectedSelector}
              >
                Test Extract
              </button>
            </div>
          </div>
        </div>
      </aside>

      {showCreditsModal && (
        <div className="kairos-modal-overlay" role="dialog" aria-modal="true">
          <div className="kairos-modal">
            <div className="kairos-modal-header">
              <h3 className="kairos-modal-title">Not enough credits</h3>

              <button
                className="kairos-modal-close"
                onClick={() => setShowCreditsModal(false)}
                aria-label="Close"
                type="button"
              >
                <FaTimesCircle />
              </button>
            </div>

            <div className="kairos-modal-body">
              <p className="kairos-modal-text">
                You don’t have enough credits to complete this action.
              </p>

              <div className="kairos-modal-metrics">
                <div className="kairos-metric">
                  <span className="kairos-metric-label">Estimated tokens</span>
                  <span className="kairos-metric-value">{Number(estimatedTokens || 0).toLocaleString()}</span>
                </div>

                {/* Optional: only show if you have estimatedCredits */}
                {estimatedCredits !== null && estimatedCredits !== undefined && (
                  <div className="kairos-metric">
                    <span className="kairos-metric-label">Estimated credits</span>
                    <span className="kairos-metric-value">{Number(estimatedCredits || 0).toLocaleString()}</span>
                  </div>
                )}

                {/* Optional: show user's current credits if you want */}
                {latestUserData?.credits !== null && latestUserData?.credits !== undefined && (
                  <div className="kairos-metric">
                    <span className="kairos-metric-label">Your credits</span>
                    <span className="kairos-metric-value">{Number(latestUserData?.credits || 0).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="kairos-modal-footer">
              <button
                className="kairos-btn ghost"
                type="button"
                onClick={() => setShowCreditsModal(false)}
              >
                Close
              </button>

              <button
                className="kairos-btn primary"
                type="button"
                onClick={() => navigate("/buy-credits")}  // ✅ change route to your real page
              >
                Buy credits
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default KairosEditor;